import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef } from "react";
import { AppEventPublisher } from "src/AppEventPublisher";
import { isImportFeedbackCohort } from "~shared/importFeedback";
import type {
  ImportFeedbackCohort,
  ImportFeedbackPayload,
  ImportFeedbackVariation,
} from "./ImportFeedback";

type ImportFeedbackFlag = {
  showImportFeedbackComponent?: boolean;
  invocationId?: string | null;
  variation?: ImportFeedbackVariation | null;
  cohort?: ImportFeedbackCohort | null;
};

type ImportFeedbackProject = {
  data?: {
    importFeedback?: ImportFeedbackFlag | null;
  };
  update: (data: { importFeedback: ImportFeedbackFlag }) => Promise<unknown>;
} | null | undefined;

function isImportFeedbackVariation(value: unknown): value is ImportFeedbackVariation {
  return value === "Classic" || value === "Agentic";
}

export function getVisibleImportFeedback(flag: ImportFeedbackFlag | null | undefined) {
  if (!flag?.showImportFeedbackComponent || !flag.invocationId || !isImportFeedbackVariation(flag.variation)) {
    return null;
  }

  return {
    invocationId: flag.invocationId,
    variation: flag.variation,
    cohort: isImportFeedbackCohort(flag.cohort) ? flag.cohort : undefined,
  };
}

export function useImportFeedbackOverlay({
  hidden,
  project,
  projectId,
}: {
  hidden: boolean;
  project: ImportFeedbackProject;
  projectId: string;
}) {
  const shownImportFeedbackKeyRef = useRef<string | null>(null);
  const importFeedbackFlag = project?.data?.importFeedback;
  const visibleImportFeedback = getVisibleImportFeedback(importFeedbackFlag);
  const showImportFeedbackOverlay = Boolean(visibleImportFeedback) && !hidden;

  const persistHiddenImportFeedback = useCallback(
    (currentFlag: ImportFeedbackFlag) => {
      if (!project) return;

      void project.update({
        importFeedback: {
          ...currentFlag,
          showImportFeedbackComponent: false,
        },
      }).catch((error: unknown) => {
        Sentry.captureException(error);
      });
    },
    [project],
  );

  const handleImportFeedbackSkip = useCallback(() => {
    const visibleFeedback = getVisibleImportFeedback(importFeedbackFlag);
    if (!importFeedbackFlag || !projectId || !visibleFeedback) return;

    AppEventPublisher.agenticChat.importFeedbackSkipped({
      projectId,
      invocationId: visibleFeedback.invocationId,
      variation: visibleFeedback.variation,
      cohort: visibleFeedback.cohort,
    });
    persistHiddenImportFeedback(importFeedbackFlag);
  }, [importFeedbackFlag, persistHiddenImportFeedback, projectId]);

  const handleImportFeedbackSubmit = useCallback(
    (feedback: ImportFeedbackPayload) => {
      const visibleFeedback = getVisibleImportFeedback(importFeedbackFlag);
      if (!importFeedbackFlag || !projectId || !visibleFeedback) return;

      AppEventPublisher.agenticChat.importFeedbackSubmitted({
        projectId,
        invocationId: visibleFeedback.invocationId,
        variation: visibleFeedback.variation,
        cohort: visibleFeedback.cohort,
        stars: feedback.rating,
        hasComment: Boolean(feedback.feedbackText?.trim()),
      });
      persistHiddenImportFeedback(importFeedbackFlag);
    },
    [importFeedbackFlag, persistHiddenImportFeedback, projectId],
  );

  const visibleImportFeedbackInvocationId = visibleImportFeedback?.invocationId;
  const visibleImportFeedbackVariation = visibleImportFeedback?.variation;
  const visibleImportFeedbackCohort = visibleImportFeedback?.cohort;

  useEffect(() => {
    if (!projectId || !showImportFeedbackOverlay || !visibleImportFeedbackInvocationId || !visibleImportFeedbackVariation) return;

    const shownKey = `${projectId}:${visibleImportFeedbackInvocationId}`;
    if (shownImportFeedbackKeyRef.current === shownKey) return;

    shownImportFeedbackKeyRef.current = shownKey;
    AppEventPublisher.agenticChat.importFeedbackShown({
      projectId,
      invocationId: visibleImportFeedbackInvocationId,
      variation: visibleImportFeedbackVariation,
      cohort: visibleImportFeedbackCohort,
    });
  }, [
    projectId,
    showImportFeedbackOverlay,
    visibleImportFeedbackCohort,
    visibleImportFeedbackInvocationId,
    visibleImportFeedbackVariation,
  ]);

  return {
    handleImportFeedbackSkip,
    handleImportFeedbackSubmit,
    showImportFeedbackOverlay,
    visibleImportFeedback,
  };
}
